/**
 * Router Configuration
 * Using TanStack Router for type-safe routing with auth protection
 */
import { lazy, Suspense } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  redirect,
  useParams,
} from '@tanstack/react-router';
import { RootLayout, AuthenticatedLayout } from './routeLayouts';
import { HomePage } from '@/features/home';
import { LoginPage } from '@/features/auth';
import { useAuthStore } from '@/store/authStore';
import { queryClient, queryKeys } from '@/services/api/queryClient';
import { fetchBrowse, fetchGlobalGraph } from '@/features/network/api/hubApi';
import { fetchCompanyDetail, fetchPersonaDetail } from '@/features/database/api/databaseApi';
import { fetchSpinoutCandidate, fetchSpinoutPersonas } from '@/features/radar/api/spinoutApi';

// ---------------------------------------------------------------------------
// Lazy-loaded feature bundles — each group becomes one separate JS chunk
// ---------------------------------------------------------------------------

const NetworkPage = lazy(() => import('@/features/network').then(m => ({ default: m.NetworkPage })));

const DatabaseLayout    = lazy(() => import('@/features/database').then(m => ({ default: m.DatabaseLayout })));
const CompaniesPage     = lazy(() => import('@/features/database').then(m => ({ default: m.CompaniesPage })));
const CompanyDetailPage = lazy(() => import('@/features/database').then(m => ({ default: m.CompanyDetailPage })));
const PeoplePage        = lazy(() => import('@/features/database').then(m => ({ default: m.PeoplePage })));
const PersonaDetailPage = lazy(() => import('@/features/database').then(m => ({ default: m.PersonaDetailPage })));
const ClusterReviewPage = lazy(() => import('@/features/database/ClusterReviewPage').then(m => ({ default: m.ClusterReviewPage })));

const RadarPage         = lazy(() => import('@/features/radar').then(m => ({ default: m.RadarPage })));
const SpinoutDetailPage = lazy(() => import('@/features/radar').then(m => ({ default: m.SpinoutDetailPage })));

const SettingsLayout      = lazy(() => import('@/features/settings').then(m => ({ default: m.SettingsLayout })));
const SettingsAccountPage = lazy(() => import('@/features/settings').then(m => ({ default: m.SettingsAccountPage })));

const AdminLayout               = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminLayout })));
const AdminOperationsHomePage   = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminOperationsHomePage })));
const AdminCostPage             = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminCostPage })));
const AdminRunsPage             = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminRunsPage })));
const AdminProvenancePage       = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminProvenancePage })));
const AdminConnectorsPage       = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminConnectorsPage })));
const AdminWaitlistPage         = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminWaitlistPage })));
const AdminEmailsPage           = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminEmailsPage })));
const AdminCampaignsPage        = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminCampaignsPage })));
const AdminAnalyticsPage        = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminAnalyticsPage })));
const AdminETLDashboardPage     = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminETLDashboardPage })));
const AdminPipelinesPage        = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminPipelinesPage })));
const AdminFlagsPage            = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminFlagsPage })));
const AdminAuditPage            = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminAuditPage })));
const AdminBackgroundJobsPage   = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminBackgroundJobsPage })));
const AdminScheduledJobsPage    = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminScheduledJobsPage })));
const AdminUsersPage            = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminUsersPage })));
const AdminAppUsersPage         = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminAppUsersPage })));
const AdminEntityResolutionPage = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminEntityResolutionPage })));

const CreateWorkspacePage   = lazy(() => import('@/features/workspaces').then(m => ({ default: m.CreateWorkspacePage })));
const AcceptInvitePage      = lazy(() => import('@/features/workspaces').then(m => ({ default: m.AcceptInvitePage })));
const WorkspaceSettingsPage = lazy(() => import('@/features/workspaces').then(m => ({ default: m.WorkspaceSettingsPage })));

const CrmLayout        = lazy(() => import('@/features/crm').then(m => ({ default: m.CrmLayout })));
const CrmPipelinePage  = lazy(() => import('@/features/crm').then(m => ({ default: m.CrmPipelinePage })));
const CrmCompaniesPage = lazy(() => import('@/features/crm').then(m => ({ default: m.CrmCompaniesPage })));
const CrmPeoplePage    = lazy(() => import('@/features/crm').then(m => ({ default: m.CrmPeoplePage })));

const AdminWorkspacesPage   = lazy(() => import('@/features/admin').then(m => ({ default: m.AdminWorkspacesPage })));

// ---------------------------------------------------------------------------
// Route-level Suspense wrapper + skeleton fallback
// ---------------------------------------------------------------------------

function PageFallback() {
  return (
    <div style={{ padding: '2rem' }}>
      <div className="skeleton" style={{ height: '1.5rem', width: '40%', marginBottom: '1rem', borderRadius: '4px' }} />
      <div className="skeleton" style={{ height: '1rem', width: '62%', marginBottom: '0.5rem', borderRadius: '4px' }} />
      <div className="skeleton" style={{ height: '1rem', width: '48%', borderRadius: '4px' }} />
    </div>
  );
}

function withSuspense(Component: React.ComponentType): () => JSX.Element {
  return () => (
    <Suspense fallback={<PageFallback />}>
      <Component />
    </Suspense>
  );
}

// ============================================
// Auth Context for Router
// ============================================

export interface RouterContext {
  auth: {
    isAuthenticated: boolean;
    sessionChecked: boolean;
  };
}

// Helper to get current auth state
function getAuthContext(): RouterContext['auth'] {
  const state = useAuthStore.getState();
  return {
    isAuthenticated: state.isAuthenticated,
    sessionChecked: state.sessionChecked,
  };
}

// ============================================
// Route adapters — key-reset wrappers
//
// TanStack Router keeps the same component instance alive when navigating
// between pages that share a route (e.g. company A → company B). This means
// useState values (active tab, sub-tab, view toggles) carry over from the
// previous entity, producing stale/stub views until the user interacts.
//
// React's `key` prop is the idiomatic fix: when the key changes React
// destroys the old tree and mounts a fresh one, resetting all state
// without any effect or cancellation logic needed.
// ============================================

function CompanyDetailRoute() {
  const { companyId } = useParams({ strict: false }) as { companyId: string };
  return (
    <Suspense fallback={<PageFallback />}>
      <CompanyDetailPage key={companyId} />
    </Suspense>
  );
}

function PersonaDetailRoute() {
  const { personaId } = useParams({ strict: false }) as { personaId: string };
  return (
    <Suspense fallback={<PageFallback />}>
      <PersonaDetailPage key={personaId} />
    </Suspense>
  );
}


// ============================================
// Route Definitions
// ============================================

// Root route
const rootRoute = createRootRoute({
  component: RootLayout,
});

// Login route (public)
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
});

// Authenticated layout route
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authenticated',
  component: AuthenticatedLayout,
  beforeLoad: async () => {
    const auth = getAuthContext();
    
    // Wait for session check to complete
    if (!auth.sessionChecked) {
      // Session check in progress - allow render (AuthProvider handles loading)
      return;
    }
    
    // Redirect to login if not authenticated
    if (!auth.isAuthenticated) {
      throw redirect({
        to: '/login',
      });
    }
  },
});

// Index route - homepage (triple command strip)
const indexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/',
  component: HomePage,
});

// Network route (protected)
const networkRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/network',
  component: withSuspense(NetworkPage),
  validateSearch: (search: Record<string, unknown>) => {
    const out: { focus?: string } = {};
    if (typeof search.focus === 'string' && search.focus) out.focus = search.focus;
    return out;
  },
  loader: () => {
    void queryClient.ensureQueryData({ queryKey: queryKeys.hub.browse(), queryFn: () => fetchBrowse() });
    void queryClient.ensureQueryData({ queryKey: queryKeys.hub.globalGraph(), queryFn: () => fetchGlobalGraph() });
  },
});

// Database route (protected) – layout with sidebar: Companies / People
const databaseRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/database',
  component: withSuspense(DatabaseLayout),
});

const databaseIndexRoute = createRoute({
  getParentRoute: () => databaseRoute,
  path: '/',
  component: () => <Navigate to="/database/companies" />,
});

const databaseCompaniesRoute = createRoute({
  getParentRoute: () => databaseRoute,
  path: 'companies',
  component: withSuspense(CompaniesPage),
  validateSearch: (search: Record<string, unknown>) => {
    const out: Record<string, string | number> = {};
    if (typeof search.q === 'string' && search.q) out.q = search.q;
    if (typeof search.status === 'string' && search.status) out.status = search.status;
    if (typeof search.j === 'string' && search.j) out.j = search.j;
    if (typeof search.regime === 'string' && search.regime) out.regime = search.regime;
    if (typeof search.src === 'string' && search.src) out.src = search.src;
    const fca = Number(search.fca);
    if (fca === 1) out.fca = 1;
    const lei = Number(search.lei);
    if (lei === 1) out.lei = 1;
    const off = Number(search.off);
    if (Number.isFinite(off) && off > 0) out.off = off;
    return out;
  },
});

const databaseClusterReviewRoute = createRoute({
  getParentRoute: () => databaseRoute,
  path: 'clusters/pending',
  component: withSuspense(ClusterReviewPage),
});

const databaseCompanyDetailRoute = createRoute({
  getParentRoute: () => databaseRoute,
  path: 'companies/$companyId',
  component: CompanyDetailRoute,
  loader: ({ params }) => {
    void queryClient.ensureQueryData({
      queryKey: queryKeys.database.company(params.companyId),
      queryFn: () => fetchCompanyDetail(params.companyId),
    });
  },
});

const databasePeopleRoute = createRoute({
  getParentRoute: () => databaseRoute,
  path: 'people',
  component: withSuspense(PeoplePage),
  validateSearch: (search: Record<string, unknown>) => {
    const out: Record<string, string | number> = {};
    if (typeof search.q === 'string' && search.q) out.q = search.q;
    const off = Number(search.off);
    if (Number.isFinite(off) && off > 0) out.off = off;
    return out;
  },
});

const databasePersonaDetailRoute = createRoute({
  getParentRoute: () => databaseRoute,
  path: 'people/$personaId',
  component: PersonaDetailRoute,
  loader: ({ params }) => {
    void queryClient.ensureQueryData({
      queryKey: queryKeys.database.persona(params.personaId),
      queryFn: () => fetchPersonaDetail(params.personaId),
    });
  },
});

// Radar route (protected)
// Filter/sort state is URL-driven so the back button restores the list.
const radarRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/radar',
  component: withSuspense(RadarPage),
  validateSearch: (search: Record<string, unknown>) => {
    // Return only keys that have values so Link-targets don't require a
    // `search` prop when linking to /radar with no filters.
    const out: Record<string, string | number> = {};
    if (typeof search.arch === 'string' && search.arch) out.arch = search.arch;
    const conf = Number(search.conf);
    if (Number.isFinite(conf)) out.conf = conf;
    if (typeof search.pQ === 'string' && search.pQ) out.pQ = search.pQ;
    if (typeof search.nQ === 'string' && search.nQ) out.nQ = search.nQ;
    if (typeof search.iFrom === 'string' && search.iFrom) out.iFrom = search.iFrom;
    if (typeof search.iTo === 'string' && search.iTo) out.iTo = search.iTo;
    if (typeof search.pCat === 'string' && search.pCat) out.pCat = search.pCat;
    if (typeof search.nCat === 'string' && search.nCat) out.nCat = search.nCat;
    if (typeof search.sort === 'string' && search.sort) out.sort = search.sort;
    if (search.ord === 'asc') out.ord = 'asc';
    return out;
  },
  loader: () => {
    const defaultParams = { min_confidence: 0.7, status: 'active', sort_by: 'latest_new_incorporated_on' as const, sort_order: 'desc' as const, limit: 100 };
    void queryClient.ensureQueryData({
      queryKey: queryKeys.radar.personas(defaultParams),
      queryFn: () => fetchSpinoutPersonas(defaultParams),
    });
  },
});

const radarDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/radar/$candidateId',
  component: withSuspense(SpinoutDetailPage),
  loader: ({ params }) => {
    void queryClient.ensureQueryData({
      queryKey: queryKeys.radar.spinout(params.candidateId),
      queryFn: () => fetchSpinoutCandidate(params.candidateId),
    });
  },
});

// Settings route (protected) – layout with sidebar
const settingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/settings',
  component: withSuspense(SettingsLayout),
});

const settingsIndexRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: '/',
  component: () => <Navigate to="/settings/account" />,
});

const settingsAccountRoute = createRoute({
  getParentRoute: () => settingsRoute,
  path: 'account',
  component: withSuspense(SettingsAccountPage),
});

// Admin center route (protected + permission gated)
const adminRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/admin',
  component: withSuspense(AdminLayout),
  beforeLoad: async () => {
    const state = useAuthStore.getState();

    // Wait for session check to complete
    if (!state.sessionChecked) return;

    // Must be authenticated (authenticatedRoute already checks, but keep defense-in-depth)
    if (!state.isAuthenticated) {
      throw redirect({ to: '/login' });
    }

    // Permission gate
    if (!state.hasPermission('admin:access')) {
      throw redirect({ to: '/network' });
    }
  },
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: withSuspense(AdminOperationsHomePage),
});

const adminWaitlistRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'waitlist',
  component: withSuspense(AdminWaitlistPage),
});

const adminEmailsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'emails',
  component: withSuspense(AdminEmailsPage),
});

const adminCampaignsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'campaigns',
  component: withSuspense(AdminCampaignsPage),
});

const adminAnalyticsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'analytics',
  component: withSuspense(AdminAnalyticsPage),
});

const adminETLRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'etl',
  component: withSuspense(AdminETLDashboardPage),
});

const adminPipelinesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'pipelines',
  component: withSuspense(AdminPipelinesPage),
  validateSearch: (search: Record<string, unknown>): { pipeline?: string; run?: string } => ({
    pipeline: (search.pipeline as string) || undefined,
    run: (search.run as string) || undefined,
  }),
});

const adminFlagsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'flags',
  component: withSuspense(AdminFlagsPage),
});

const adminAuditRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'audit',
  component: withSuspense(AdminAuditPage),
});

const adminBackgroundJobsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'background-jobs',
  component: withSuspense(AdminBackgroundJobsPage),
});

const adminCostRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'cost',
  component: withSuspense(AdminCostPage),
});

const adminRunsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'runs',
  component: withSuspense(AdminRunsPage),
});

const adminProvenanceRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'provenance',
  component: withSuspense(AdminProvenancePage),
});

const adminConnectorsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'connectors',
  component: withSuspense(AdminConnectorsPage),
});

const adminScheduledJobsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'scheduled-jobs',
  component: withSuspense(AdminScheduledJobsPage),
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'users',
  component: withSuspense(AdminUsersPage),
});

const adminAppUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'app-users',
  component: withSuspense(AdminAppUsersPage),
});

const adminErReviewRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'er-review',
  component: withSuspense(AdminEntityResolutionPage),
});

const adminWorkspacesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'workspaces',
  component: withSuspense(AdminWorkspacesPage),
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === 'string' ? search.q : undefined,
  }),
});

// CRM routes (workspace-scoped)
const crmRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/crm',
  component: withSuspense(CrmLayout),
});

const crmIndexRoute = createRoute({
  getParentRoute: () => crmRoute,
  path: '/',
  component: () => <Navigate to="/crm/pipeline" />,
});

const crmPipelineRoute = createRoute({
  getParentRoute: () => crmRoute,
  path: 'pipeline',
  component: withSuspense(CrmPipelinePage),
});

const crmCompaniesRoute = createRoute({
  getParentRoute: () => crmRoute,
  path: 'companies',
  component: withSuspense(CrmCompaniesPage),
});

const crmPeopleRoute = createRoute({
  getParentRoute: () => crmRoute,
  path: 'people',
  component: withSuspense(CrmPeoplePage),
});

// Workspace routes (admin-only — members/viewers have no visibility into workspace management)
const workspaceNewRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/workspaces/new',
  component: withSuspense(CreateWorkspacePage),
  beforeLoad: async () => {
    const state = useAuthStore.getState();
    if (!state.hasPermission('admin:workspaces:manage')) {
      throw redirect({ to: '/' });
    }
  },
});

const workspaceAcceptRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/workspaces/accept',
  component: withSuspense(AcceptInvitePage),
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === 'string' ? search.token : undefined,
  }),
});

const workspaceSettingsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: '/workspaces/$workspaceSlug/settings',
  component: withSuspense(WorkspaceSettingsPage),
});

// ============================================
// Route Tree
// ============================================

const routeTree = rootRoute.addChildren([
  loginRoute,
  authenticatedRoute.addChildren([
    indexRoute,
    networkRoute,
    databaseRoute.addChildren([
      databaseIndexRoute,
      databaseCompaniesRoute,
      databaseCompanyDetailRoute,
      databasePeopleRoute,
      databasePersonaDetailRoute,
      databaseClusterReviewRoute,
    ]),
    radarRoute,
    radarDetailRoute,
    settingsRoute.addChildren([
      settingsIndexRoute,
      settingsAccountRoute,
    ]),
    crmRoute.addChildren([
      crmIndexRoute,
      crmPipelineRoute,
      crmCompaniesRoute,
      crmPeopleRoute,
    ]),
    workspaceNewRoute,
    workspaceAcceptRoute,
    workspaceSettingsRoute,
    adminRoute.addChildren([
      adminIndexRoute,
      adminWaitlistRoute,
      adminEmailsRoute,
      adminCampaignsRoute,
      adminAnalyticsRoute,
      adminETLRoute,
      adminPipelinesRoute,
      adminFlagsRoute,
      adminAuditRoute,
      adminBackgroundJobsRoute,
      adminCostRoute,
      adminRunsRoute,
      adminProvenanceRoute,
      adminConnectorsRoute,
      adminScheduledJobsRoute,
      adminUsersRoute,
      adminAppUsersRoute,
      adminErReviewRoute,
      adminWorkspacesRoute,
    ]),
  ]),
]);

// ============================================
// Router Instance
// ============================================

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  context: {
    auth: {
      isAuthenticated: false,
      sessionChecked: false,
    },
  },
});

// Register router for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
